import React, { useEffect, useState, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../components/ui/Toast';
import {
  platformConsumerService,
  platformFieldRegistryService,
  platformDefinitionService,
} from '../../../services/apiService';
import { parseApiError, fieldError } from '../apiErrors';
import { Overlay, Field, ErrorBanner, SensitivityBadge, inputCls, FErr } from '../_components/common';

const ENTITIES = [
  { value: 'Vendor', label: 'Vendor', icon: 'Building2' },
  { value: 'Approval', label: 'Approval', icon: 'CheckCircle' },
  { value: 'Document', label: 'Document', icon: 'FileText' },
  { value: 'AuditLog', label: 'Audit Log', icon: 'ScrollText' },
];
const STEPS = ['Basics', 'Entity', 'Fields', 'Auth & limits', 'Review'];

const Review = ({ label, value }) => (
  <div className="flex gap-3">
    <span className="w-28 shrink-0 text-slate-400">{label}</span>
    <span className="text-slate-800 flex-1 break-words">{value || '—'}</span>
  </div>
);

const FieldGroup = ({ title, hint, tone, fields, selectedSet, onToggle }) => {
  if (!fields || fields.length === 0) return null;
  const toneCls = tone === 'restricted' ? 'border-red-300 bg-red-50/50' : tone === 'pii' ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200';
  const titleCls = tone === 'restricted' ? 'text-red-700' : tone === 'pii' ? 'text-blue-700' : 'text-slate-500';
  return (
    <div className={`rounded-lg border ${toneCls}`}>
      <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-200/70">
        <span className={`text-xs font-semibold uppercase tracking-wide ${titleCls}`}>{title}</span>
        {hint && <span className="text-xs text-slate-400">— {hint}</span>}
      </div>
      <div className="divide-y divide-slate-100">
        {fields.map((f) => (
          <label key={f.id} className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-white/70">
            <input type="checkbox" checked={selectedSet.has(f.id)} onChange={() => onToggle(f.id)} className="mt-1" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-800 text-sm">{f.apiFieldName}</span>
                <span className="text-[10px] font-mono text-slate-400">{f.dataType}</span>
                <SensitivityBadge sensitivity={f.sensitivity} />
              </div>
              {f.description && <p className="text-xs text-slate-500 mt-0.5">{f.description}</p>}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

const DefinitionWizard = ({ onClose, onCreated }) => {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [consumers, setConsumers] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', consumerId: '', entity: '', selected: [], justification: '',
    authMode: 'ip_and_signature', rateLimitPerMin: 60,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const res = await platformConsumerService.list({ page: 1, limit: 100 });
        setConsumers((res.data || []).filter((c) => c.status === 'active'));
      } catch (err) {
        setError(parseApiError(err));
      }
    })();
  }, []);

  const loadFields = async (entity) => {
    setFieldsLoading(true);
    try {
      const res = await platformFieldRegistryService.list(entity);
      setFields(res.data || []);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setFieldsLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const g = { restricted: [], pii: [], none: [] };
    for (const f of fields) (g[f.sensitivity] || g.none).push(f);
    return g;
  }, [fields]);
  const selectedSet = useMemo(() => new Set(form.selected), [form.selected]);
  const hasRestricted = useMemo(() => fields.some((f) => selectedSet.has(f.id) && f.sensitivity === 'restricted'), [fields, selectedSet]);
  const selectedFields = useMemo(() => fields.filter((f) => selectedSet.has(f.id)), [fields, selectedSet]);

  const toggleField = (id) => set('selected', selectedSet.has(id) ? form.selected.filter((x) => x !== id) : [...form.selected, id]);
  const chooseEntity = (entity) => {
    if (entity !== form.entity) {
      setForm((f) => ({ ...f, entity, selected: [], justification: '' }));
      setFields([]);
      loadFields(entity);
    }
  };

  const canNext = () => {
    if (step === 0) return !!form.name.trim() && !!form.consumerId;
    if (step === 1) return !!form.entity;
    if (step === 2) return form.selected.length >= 1 && (!hasRestricted || form.justification.trim().length >= 10);
    if (step === 3) return Number(form.rateLimitPerMin) > 0 && Number.isInteger(Number(form.rateLimitPerMin));
    return true;
  };

  const create = async () => {
    if (saving) return;
    setError(null);
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        consumerId: form.consumerId,
        entity: form.entity,
        selectedFieldIds: form.selected,
        authMode: form.authMode,
        rateLimitPerMin: Number(form.rateLimitPerMin),
        ...(hasRestricted ? { restrictedFieldJustification: form.justification.trim() } : {}),
      };
      const res = await platformDefinitionService.create(body);
      toast.success('Draft API definition created');
      onCreated(res.data);
    } catch (err) {
      const e = parseApiError(err);
      setError(e);
      // Jump back to the step most relevant to the error.
      if (Object.keys(e.details || {}).some((k) => k.startsWith('selectedFieldIds')) || e.code === 'INVALID_FIELD_SELECTION' || e.code === 'JUSTIFICATION_REQUIRED') setStep(2);
      else if (e.code === 'CONSUMER_NOT_ACTIVE' || e.code === 'CONSUMER_NOT_FOUND') setStep(0);
    } finally {
      setSaving(false);
    }
  };

  const consumerName = consumers.find((c) => c.id === form.consumerId)?.name;

  return (
    <Overlay onClose={saving ? undefined : onClose}>
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">New API definition</h2>
            <button onClick={onClose} disabled={saving} className="text-slate-400 hover:text-slate-600"><Icon name="X" size={18} /></button>
          </div>
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-1.5 text-xs font-medium ${i === step ? 'text-indigo-600' : i < step ? 'text-emerald-600' : 'text-slate-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i === step ? 'bg-indigo-600 text-white' : i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                    {i < step ? <Icon name="Check" size={11} /> : i + 1}
                  </span>
                  {s}
                </div>
                {i < STEPS.length - 1 && <span className="w-3 h-px bg-slate-200" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          <ErrorBanner error={error} onClose={() => setError(null)} />

          {step === 0 && (
            <>
              <Field label="API name" required>
                <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Vendor read API for Acme" autoFocus />
                {fieldError(error?.details, 'name') && <FErr>{fieldError(error.details, 'name')}</FErr>}
              </Field>
              <Field label="Consumer" required hint={consumers.length === 0 ? 'No active consumers — create or reactivate one first.' : 'Only active consumers can be selected.'}>
                <select className={inputCls} value={form.consumerId} onChange={(e) => set('consumerId', e.target.value)}>
                  <option value="">Select a consumer…</option>
                  {consumers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.environment})</option>)}
                </select>
              </Field>
            </>
          )}

          {step === 1 && (
            <div>
              <p className="text-sm text-slate-500 mb-3">Which entity should this API expose?</p>
              <div className="grid grid-cols-2 gap-3">
                {ENTITIES.map((e) => (
                  <button key={e.value} type="button" onClick={() => chooseEntity(e.value)}
                    className={`flex items-center gap-3 p-4 rounded-lg border text-left transition-colors ${form.entity === e.value ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <Icon name={e.icon} size={20} className={form.entity === e.value ? 'text-indigo-600' : 'text-slate-400'} />
                    <span className="font-medium text-slate-800">{e.label}</span>
                    {form.entity === e.value && <Icon name="Check" size={16} className="ml-auto text-indigo-600" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-sm text-slate-500 mb-2">Select the fields to expose ({form.selected.length} selected). Restricted fields are highlighted and require a justification.</p>
              {fieldsLoading ? (
                <div className="py-10 text-center text-slate-400"><Icon name="Loader" className="animate-spin inline mr-2" size={16} />Loading fields…</div>
              ) : (
                <div className="space-y-4">
                  <FieldGroup title="Restricted" hint="financial / tax data" tone="restricted" fields={grouped.restricted} selectedSet={selectedSet} onToggle={toggleField} />
                  <FieldGroup title="PII" tone="pii" fields={grouped.pii} selectedSet={selectedSet} onToggle={toggleField} />
                  <FieldGroup title="Standard" tone="none" fields={grouped.none} selectedSet={selectedSet} onToggle={toggleField} />
                  {fieldError(error?.details, 'selectedFieldIds') && <FErr>{fieldError(error.details, 'selectedFieldIds')}</FErr>}
                  {Object.entries(error?.details || {}).filter(([k]) => k.startsWith('selectedFieldIds.')).map(([k, v], i) => <FErr key={i}>{Array.isArray(v) ? v[0] : v}</FErr>)}
                </div>
              )}
              {hasRestricted && (
                <div className="mt-5 p-4 rounded-lg border border-red-200 bg-red-50">
                  <Field label="Justification for restricted fields" required hint="Minimum 10 characters — why this consumer needs the restricted field(s).">
                    <textarea rows={3} className={inputCls} value={form.justification} onChange={(e) => set('justification', e.target.value)} placeholder="e.g. Finance needs PAN for quarterly tax reconciliation." />
                    {form.justification.trim().length > 0 && form.justification.trim().length < 10 && <FErr>At least 10 characters.</FErr>}
                    {error?.code === 'JUSTIFICATION_REQUIRED' && <FErr>{error.message}</FErr>}
                  </Field>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <>
              <Field label="Authentication mode" required>
                <select className={inputCls} value={form.authMode} onChange={(e) => set('authMode', e.target.value)}>
                  <option value="ip_and_signature">IP + bearer secret (ip_and_signature)</option>
                  <option value="mtls">mTLS (not yet enforced by the gateway)</option>
                </select>
              </Field>
              <Field label="Rate limit (requests per minute)" required>
                <input type="number" min={1} className={inputCls} value={form.rateLimitPerMin} onChange={(e) => set('rateLimitPerMin', e.target.value)} />
                {!(Number(form.rateLimitPerMin) > 0 && Number.isInteger(Number(form.rateLimitPerMin))) && <FErr>Must be a positive whole number.</FErr>}
              </Field>
            </>
          )}

          {step === 4 && (
            <div className="space-y-3 text-sm">
              <Review label="Name" value={form.name} />
              <Review label="Consumer" value={consumerName} />
              <Review label="Entity" value={form.entity} />
              <Review label="Fields" value={<div className="flex flex-wrap gap-1.5">{selectedFields.map((f) => <span key={f.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{f.apiFieldName}<SensitivityBadge sensitivity={f.sensitivity} /></span>)}</div>} />
              {hasRestricted && <Review label="Justification" value={form.justification} />}
              <Review label="Auth mode" value={form.authMode} />
              <Review label="Rate limit" value={`${form.rateLimitPerMin} / min`} />
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800 text-xs flex items-start gap-2">
                <Icon name="Info" size={14} className="mt-0.5 shrink-0" />
                <span>Creates a <strong>draft</strong>. No key or secret is generated until you Activate it.</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <Button variant="ghost" onClick={step === 0 ? onClose : () => setStep(step - 1)} disabled={saving}>{step === 0 ? 'Cancel' : 'Back'}</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canNext()} iconName="ArrowRight" iconPosition="right">Next</Button>
          ) : (
            <Button onClick={create} loading={saving} iconName="Check" iconPosition="right">Create draft</Button>
          )}
        </div>
      </div>
    </Overlay>
  );
};

export default DefinitionWizard;
